import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Landmark, Loader2, CheckCircle2, ShieldCheck, Vote } from "lucide-react";

export default function PublicVote() {
  const { token } = useParams();
  const [poll, setPoll] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState("verify");
  const [name, setName] = useState("");
  const [ic, setIc] = useState("");
  const [member, setMember] = useState(null);
  const [choices, setChoices] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/public/vote/${token}`).then((r) => setPoll(r.data.poll)).catch(() => setNotFound(true)); }, [token]);

  const toggle = (id) => {
    setError("");
    if (choices.includes(id)) return setChoices(choices.filter((c) => c !== id));
    if (choices.length >= poll.max_choices) {
      if (poll.max_choices === 1) return setChoices([id]);
      return setError(`Maksimum ${poll.max_choices} pilihan`);
    }
    setChoices([...choices, id]);
  };

  const verify = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const r = await api.post(`/public/vote/${token}/verify`, { name: name.trim(), ic: ic.trim() });
      if (r.data.already_voted) { setError("Anda telah pun mengundi. / You have already voted."); setLoading(false); return; }
      setMember(r.data); setStep("vote");
    } catch (err) { setError(apiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const submit = async () => {
    if (choices.length === 0) return setError("Sila pilih sekurang-kurangnya satu");
    setError(""); setLoading(true);
    try {
      await api.post(`/public/vote/${token}/submit`, { name: name.trim(), ic: ic.trim(), choices });
      setStep("done");
    } catch (err) { setError(apiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  if (notFound) return <Shell><p className="text-brick text-center py-8">Link undian tidak sah. / Invalid voting link.</p></Shell>;
  if (!poll) return <Shell><div className="flex justify-center py-8"><Loader2 className="animate-spin text-brass" size={26} /></div></Shell>;

  return (
    <Shell>
      <div className="mb-5">
        <div className="flex items-center gap-2 text-brass text-xs uppercase tracking-wider mb-2"><Vote size={14} /> Undian / Voting</div>
        <h1 className="font-serif text-2xl text-ink">{poll.title}</h1>
        {poll.description && <p className="text-sm text-slate mt-2">{poll.description}</p>}
        {poll.status !== "open" && <div className="mt-3 bg-brass/10 text-brass border border-brass/20 rounded-sm px-3 py-2 text-sm">Undian belum dibuka / tutup. / Voting is not open.</div>}
      </div>

      {error && <div className="mb-4 bg-brick-bg text-brick border border-brick/20 px-3 py-2 rounded-sm text-sm" data-testid="vote-error">{error}</div>}

      {step === "verify" && poll.status === "open" && (
        <form onSubmit={verify} className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-slate bg-brass/5 border border-brass/20 rounded-sm px-3 py-2"><ShieldCheck size={15} className="text-brass" /> Sahkan diri untuk mengundi / Verify to vote</div>
          <div><label className="block text-xs font-medium text-slate mb-1">Nama Penuh / Full Name</label><input required value={name} onChange={(e) => setName(e.target.value)} data-testid="vote-name" placeholder="Nama seperti dalam rekod" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-brass text-ink" /></div>
          <div><label className="block text-xs font-medium text-slate mb-1">No. Kad Pengenalan / IC Number</label><input required value={ic} onChange={(e) => setIc(e.target.value)} data-testid="vote-ic" placeholder="900101-14-5533" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2.5 text-sm font-mono outline-none focus:border-brass text-ink" /></div>
          <button type="submit" disabled={loading} data-testid="vote-verify-btn" className="w-full bg-ink text-white hover:bg-ink-light py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">{loading && <Loader2 size={16} className="animate-spin" />} Teruskan / Continue</button>
        </form>
      )}

      {step === "vote" && member && (
        <div className="space-y-4">
          <p className="text-sm text-ink">Salam <strong>{member.name}</strong> — pilih sehingga <strong>{poll.max_choices}</strong> ({choices.length} dipilih)</p>
          <div className="space-y-2">
            {poll.candidates.map((c) => (
              <button type="button" key={c.id} onClick={() => toggle(c.id)} data-testid={`vote-cand-${c.id}`}
                className={`w-full text-left border-2 rounded-sm px-3 py-3 transition-colors flex items-center gap-3 ${choices.includes(c.id) ? "border-brass bg-brass/10" : "border-hairline hover:border-slate/40"}`}>
                {c.photo ? <img src={c.photo} alt={c.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-hairline" /> : <div className="w-12 h-12 rounded-full bg-ink/10 flex items-center justify-center text-ink font-serif flex-shrink-0">{c.name?.[0]}</div>}
                <div className="flex-1 min-w-0"><div className="font-medium text-sm text-ink">{c.name}</div>{c.info && <div className="text-xs text-slate">{c.info}</div>}{(c.pencadang || c.penyokong) && <div className="text-[10px] text-slate/70 mt-0.5">{c.pencadang ? `Pencadang: ${c.pencadang}` : ""}{c.pencadang && c.penyokong ? " · " : ""}{c.penyokong ? `Penyokong: ${c.penyokong}` : ""}</div>}</div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${choices.includes(c.id) ? "border-brass bg-brass" : "border-slate/30"}`}>{choices.includes(c.id) && <CheckCircle2 size={16} className="text-ink" />}</div>
              </button>
            ))}
          </div>
          <button onClick={submit} disabled={loading} data-testid="vote-submit-btn" className="w-full bg-brass text-ink font-semibold hover:bg-brass-hover py-2.5 rounded-sm text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">{loading && <Loader2 size={16} className="animate-spin" />} Hantar Undi / Submit Vote</button>
        </div>
      )}

      {step === "done" && (
        <div className="text-center py-8" data-testid="vote-done">
          <CheckCircle2 size={48} className="text-positive mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-ink">Terima kasih!</h2>
          <p className="text-sm text-slate mt-2">Undi anda telah direkodkan.<br />Your vote has been recorded.</p>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-5"><Landmark className="text-brass" strokeWidth={1.5} size={26} /><span className="font-serif text-xl text-paper">KKPKLB</span></div>
        <div className="bg-white rounded-md shadow-2xl p-7">{children}</div>
        <p className="text-center text-paper/40 text-xs mt-4">Koperasi Kumpulan Profesional Kuala Lumpur Berhad</p>
      </div>
    </div>
  );
}
